import React, { useState } from "react";
import { Eye, EyeOff, Mail, Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SocialAuthentication from "./SocialAuthentication";
import config from '../config/config';

function LoginForm() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ email: "", password: "" });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [focusedField, setFocusedField] = useState(null);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await new Promise(resolve => setTimeout(resolve, 1500));
            await axios.get(config.API_URL + "/sanctum/csrf-cookie", {
                withCredentials: true,
            });

            const getCookie = (name) => {
                const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
                if (match) return decodeURIComponent(match[2]);
                return null;
            };
            const xsrfToken = getCookie("XSRF-TOKEN");

            const response = await axios.post(
                config.API_URL + "/api/apiLogin",
                formData,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "X-XSRF-TOKEN": xsrfToken,
                    },
                    withCredentials: true,
                }
            );

            localStorage.setItem("token", response.data.token);
            localStorage.setItem("userInfor", response.data.user);
          
            if(response.data.status==1){
                setSuccess("Successfully logged in");
                 navigate("/dashboard");
            }else{
                setError("Login failed - Invalid credentials. Please try again.");
                
                 navigate("/");
            }
           
        } catch (error) {
            setError("Login failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="h-screen w-screen bg-[#010132] flex flex-col justify-center items-center p-4 sm:p-6 overflow-auto">
            {/* Floating particles background - hidden on mobile */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute animate-pulse"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`,
                            animationDuration: `${3 + Math.random() * 2}s`
                        }}
                    >
                        <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-30"></div>
                    </div>
                ))}
            </div>

            <div className="relative w-full max-w-full sm:max-w-md">
                {/* Main login card */}
                <div className="bg-white backdrop-blur-xl rounded-none sm:rounded-3xl shadow-2xl border border-white/20 p-4 sm:p-8 relative overflow-hidden flex flex-col">
                    {/* Background gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none"></div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                        {/* Header */}
                        <div className="text-center mb-4 sm:mb-8">
                            <a href="https://joharischool.co.ke"> 
                           <center>
                                <img src="https://joharischool.co.ke/wp-content/uploads/2022/01/download.png" className="rounded" style={{ width: '120px', height: '100px' }} className="sm:w-[100px] sm:h-[100px]" />
                            </center>
                            <h1 className="text-xl mt-3 sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
                                School Finance
                            </h1></a>
                            <p className="text-gray-600 font-medium text-sm sm:text-base">Account Access</p>
                            <div className="w-16 h-1 bg-[#ff7713] rounded-full mx-auto mt-2 sm:mt-3"></div>

                        </div>

                        {/* Social Authentication */}
                        <div className="mb-4 sm:mb-8">
                            <SocialAuthentication />
                        </div>

                        {/* Divider */}
                        <div className="relative mb-4 sm:mb-8">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200"></div>
                            </div>
                        </div>

                        {/* Alerts */}
                        {success && (
                            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-xl flex items-center space-x-3">
                                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                <span className="text-green-700 font-medium text-sm sm:text-base">{success}</span>
                            </div>
                        )}
                        {error && (
                            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
                                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                <span className="text-red-700 font-medium text-sm sm:text-base">{error}</span>
                            </div>
                        )}

                        {/* Form */}
                        <div className="space-y-4 sm:space-y-6">
                            {/* Email Input */}
                            <div className="relative">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Email Address
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                                        <Mail className={`w-5 h-5 transition-colors ${
                                            focusedField === 'email' ? 'text-blue-500' : 'text-gray-400'
                                        }`} />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Enter your email"
                                        autoComplete="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        onFocus={() => setFocusedField('email')}
                                        onBlur={() => setFocusedField(null)}
                                        className="w-full pl-10 sm:pl-12 pr-4 py-2 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 group-hover:border-gray-300 text-sm sm:text-base"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Password Input */}
                            <div className="relative">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Password
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                                        <Lock className={`w-5 h-5 transition-colors ${
                                            focusedField === 'password' ? 'text-blue-500' : 'text-gray-400'
                                        }`} />
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        placeholder="Enter your password"
                                        autoComplete="current-password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        onFocus={() => setFocusedField('password')}
                                        onBlur={() => setFocusedField(null)}
                                        className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 group-hover:border-gray-300 text-sm sm:text-base"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            {/* Submit Button */}
                          <button
  type="submit"
  disabled={loading}
  onClick={handleSubmit}
  style={{ backgroundColor: 'rgb(2, 3, 129)' }}
  className="w-full text-white font-semibold py-3 sm:py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2 text-sm sm:text-base"
>
  {loading ? (
    <>
      <Loader2 className="w-5 h-5 animate-spin" />
      <span>Please wait...</span>
    </>
  ) : (
    <>
      <Lock className="w-5 h-5" />
      <span>Sign In</span>
    </>
  )}
</button>

                        </div>

                        {/* Footer */}
                        <div className="mt-4 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-100">
                            <div className="text-center">
                               
                               
                            </div>
                        </div>
                    </div>
                </div>

                {/* Floating elements - hidden on mobile */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full opacity-20 animate-pulse hidden md:block"></div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full opacity-15 animate-pulse hidden md:block" style={{ animationDelay: '1s' }}></div>
            </div>
        </div>
    );
}

export default LoginForm;